import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Button,
  Stack,
  IconButton,
  Divider,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link as MuiLink,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Slider,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AccessTime as ClockIcon,
  Search as SearchIcon,
  Star as StarIcon,
  Map as MapIcon,
  Layers as LayersIcon,
  ChevronRight as ChevronRightIcon,
  Warning as AlertCircleIcon,
  StarBorder as StarBorderIcon,
  LocalGasStation as FuelIcon,
  HealthAndSafety as HealthIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Assignment as AssignmentIcon,
  Speed as LiveIcon,
  CalendarToday,
  Lightbulb as LightbulbIcon,
  PowerSettingsNew as PowerIcon
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';

import 'leaflet/dist/leaflet.css';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

import towerLightService from '../../services/towerLightService';
import { formatToIST } from '../../utils/dataUtils';

// Register ChartJS
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

// Fix Leaflet marker icons
let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

const TowerLightPage = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();

  // State
  const [deviceDetails, setDeviceDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [controlLoading, setControlLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [mapType, setMapType] = useState('satellite');
  const [view, setView] = useState('dashboard'); // 'dashboard' | 'logs'

  // Local Control State (Decoupled from lagging backend)
  const [isOn, setIsOn] = useState(false);
  const [intensity, setIntensity] = useState(0);

  // Fetch logic
  const fetchDetails = useCallback(async () => {
    try {
      if (!deviceId) return;
      // Do NOT set loading to true here if we want to poll silently in future
      // But for initial load, yes.
      // We'll rely on the initial load flag or just keep it simple.
      const res = await towerLightService.getDeviceById(deviceId);
      setDeviceDetails(res.data || res);
    } catch (err) {
      console.error('Failed to fetch Tower Light details:', err);
    } finally {
      setLoadingDetails(false);
    }
  }, [deviceId]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  // Sync Local State with Backend (Initial Load / External Updates)
  useEffect(() => {
    if (deviceDetails?.latestTelemetry) {
      // Only update if we aren't currently "controlling" (optional optimization, 
      // but here we just trust the backend if it changes significantly OR we assume backend is truth).
      // To fix "snap back", we will allow this sync, BUT we won't trigger a fetch 
      // immediately after our own action.
      setIsOn(deviceDetails.latestTelemetry.status === 'ON');
      setIntensity(parseInt(deviceDetails.latestTelemetry.regulator_level) || 0);
    }
  }, [deviceDetails]);

  // Derived state
  const device = useMemo(() => deviceDetails?.device, [deviceDetails]);

  // Control Handler
  const handleControl = async (command, value) => {
    try {
      setControlLoading(true);

      // 1. Update Local State Immediately (The "Variable" Step)
      if (command === 'POWER') {
        setIsOn(value === 'ON');
        // If turning off, maybe zero out intensity visually? Optional.
      } else if (command === 'INTENSITY') {
        setIntensity(parseInt(value));
      }

      // 2. Send Command to Backend
      await towerLightService.sendCommand(deviceId, command, value);

      // 3. NO FETCH HERE. 
      // We trust our local state. The backend will eventually catch up 
      // via a natural refresh or polling mechanism (if added).
      // Fetching too soon causes the "old" DB state to overwrite our "new" local state.

    } catch (error) {
      console.error("Failed to send command", error);
      // On error, we might want to revert, or just let the next fetch fix it.
    } finally {
      setControlLoading(false);
    }
  };

  // Sensors processing
  const sensors = useMemo(() => {
    if (!deviceDetails?.sensors) return [];
    return deviceDetails.sensors.map(sensor => ({
      ...sensor,
      // Ensure history is array
      history: Array.isArray(sensor.history) ? sensor.history : []
    }));
  }, [deviceDetails]);

  const filteredSensors = useMemo(() => sensors.filter(s =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase())
  ), [sensors, searchTerm]);

  // Utilization Chart Data (Mock or Real)
  const utilizationData = useMemo(() => {
    if (!deviceDetails?.utilization) return { labels: [], datasets: [] };
    const labels = deviceDetails.utilization.map(u => new Date(u.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }));
    return {
      labels,
      datasets: [
        {
          label: 'Working',
          data: deviceDetails.utilization.map(u => u.working_hours),
          backgroundColor: '#f59e0b', // Amber for lights
          borderRadius: 4,
        },
        {
          label: 'Stopped',
          data: deviceDetails.utilization.map(u => u.stopped_hours),
          backgroundColor: '#e5e7eb',
          borderRadius: 4,
        }
      ]
    };
  }, [deviceDetails]);

  // --- RENDER HELPERS ---

  if (!deviceId) return <div />; // Or redirect to list view if implemented locally

  if (loadingDetails && !device) return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="80vh" gap={2}>
      <Skeleton variant="circular" width={60} height={60} />
      <Typography variant="body1" color="text.secondary" fontWeight={600}>Loading Light Command...</Typography>
    </Box>
  );

  return (
    <Box p={3} className="animate-fade-in">
      {/* Breadcrumbs — Tower Lights is the root */}
      <Breadcrumbs separator={<ChevronRightIcon fontSize="small" />} sx={{ mb: 3 }}>
        <MuiLink component="button" onClick={() => navigate('/tower-lights')} underline="hover" color="inherit" sx={{ fontWeight: 600 }}>Tower Lights</MuiLink>
        <Typography color="text.primary" sx={{ fontWeight: 700 }}>{deviceId}</Typography>
      </Breadcrumbs>

      {/* HEADER CARD */}
      <Card sx={{ mb: 4, borderRadius: '24px' }}>
        <CardContent sx={{ p: '20px !important' }}>
          <Box display="flex" alignItems="center" gap={3}>
            {/* 1. Image */}
            <Box sx={{ width: 80, height: 80, bgcolor: 'grey.50', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1, border: '1px solid', borderColor: 'divider' }}>
              <LightbulbIcon sx={{ fontSize: 40, color: 'warning.main' }} />
            </Box>

            {/* 2. Identity */}
            <Box>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>Dingli • Tower Light</Typography>
                <Chip label="ONLINE" color="success" size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 800, borderRadius: '6px' }} />
              </Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mt={0.5}>
                Model TL-4000 • {device?.device_type || 'TOWERLIGHT'}
              </Typography>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ mx: 1, height: 40, alignSelf: 'center' }} />

            {/* 3. Metrics Strip */}
            <Box display="flex" alignItems="center" gap={4} flexGrow={1}>
              {/* Runtime */}
              <Box>
                <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
                  <ClockIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                  <Typography variant="caption" fontWeight={700} color="text.secondary">RUNTIME</Typography>
                </Stack>
                <Typography variant="h6" fontWeight={800} lineHeight={1}>124 <Typography component="span" variant="caption" color="text.secondary" fontWeight={600}>h</Typography></Typography>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ height: 30, alignSelf: 'center' }} />

              {/* Fuel */}
              <Box>
                <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
                  <FuelIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                  <Typography variant="caption" fontWeight={700} color="text.secondary">FUEL</Typography>
                </Stack>
                <Typography variant="h6" fontWeight={800} lineHeight={1}>45 <Typography component="span" variant="caption" color="text.secondary" fontWeight={600}>L</Typography></Typography>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ height: 30, alignSelf: 'center' }} />

              {/* Health */}
              <Box>
                <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
                  <HealthIcon sx={{ fontSize: 16, color: 'success.main' }} />
                  <Typography variant="caption" fontWeight={700} color="text.secondary">HEALTH</Typography>
                </Stack>
                <Typography variant="h6" fontWeight={800} lineHeight={1}>GOOD</Typography>
              </Box>
            </Box>

            {/* 4. Live Actions */}
            <Stack direction="row" spacing={1.5}>
              <Button
                variant={view === 'dashboard' ? 'contained' : 'outlined'}
                color="warning" // Tower light theme
                onClick={() => setView('dashboard')}
                startIcon={<LiveIcon />}
                sx={{ borderRadius: '10px', fontWeight: 700, textTransform: 'none' }}
              >
                Control Panel
              </Button>
            </Stack>

          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3} className="animate-fade-in">
        {/* Left: Map & Utilization */}
        <Grid item xs={12} md={6} lg={5}>
          <Stack spacing={3}>
            {/* Map Card */}
            <Card sx={{ height: 500, display: 'flex', flexDirection: 'column' }}>
              <Box p={2} display="flex" justifyContent="space-between" alignItems="center" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <MapIcon color="primary" />
                  <Typography variant="subtitle2" fontWeight={800}>Live Tracking</Typography>
                </Box>
                <Stack direction="row" spacing={1} bgcolor="grey.100" p={0.5} borderRadius="10px">
                  <Button size="small" onClick={() => setMapType('satellite')} variant={mapType === 'satellite' ? 'contained' : 'text'} sx={{ fontSize: '10px', minWidth: 'auto', px: 1 }}>SAT</Button>
                  <Button size="small" onClick={() => setMapType('map')} variant={mapType === 'map' ? 'contained' : 'text'} sx={{ fontSize: '10px', minWidth: 'auto', px: 1 }}>MAP</Button>
                </Stack>
              </Box>
              <Box sx={{ flexGrow: 1, position: 'relative' }}>
                <MapContainer center={[device?.location?.lat || 25.2048, device?.location?.lon || 55.2708]} zoom={13} style={{ height: '100%', width: '100%' }}>
                  <TileLayer url={mapType === 'satellite' ? "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"} />
                  <Marker position={[device?.location?.lat || 25.2048, device?.location?.lon || 55.2708]} />
                </MapContainer>
                <Box sx={{ position: 'absolute', bottom: 16, left: 16, zIndex: 1000 }}>
                  <Chip label="Asset Secured" size="small" color="success" sx={{ fontWeight: 800, bgcolor: 'rgba(16, 185, 129, 0.9)', backdropFilter: 'blur(4px)' }} />
                </Box>
              </Box>
            </Card>

            {/* Utilization Card */}
            <Card sx={{ height: 350 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" mb={4}>
                  <Box>
                    <Typography variant="h6" fontWeight={800}>Metrics</Typography>
                    <Stack direction="row" spacing={3} mt={1}>
                      <Box display="flex" alignItems="center" gap={1}><Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#f59e0b' }} /><Typography variant="caption" fontWeight={700}>Active</Typography></Box>
                      <Box display="flex" alignItems="center" gap={1}><Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'grey.300' }} /><Typography variant="caption" fontWeight={700}>Idle</Typography></Box>
                    </Stack>
                  </Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Last 30 Days</Typography>
                </Box>
                <Box height={200}>
                  <Bar data={utilizationData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { stacked: true, grid: { display: false } }, y: { stacked: true, beginAtZero: true } } }} />
                </Box>
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        {/* Middle: Diagnostics & Control */}
        <Grid item xs={12} md={6} lg={4}>
          <Card sx={{ height: 874, display: 'flex', flexDirection: 'column' }}>

            {/* REMOTE CONTROL SECTION */}
            <Box p={3} bgcolor="warning.50" borderBottom="1px solid" borderColor="divider">
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle1" fontWeight={800} color="warning.main" display="flex" alignItems="center" gap={1}>
                  <PowerIcon /> Remote Operations
                </Typography>
                {controlLoading && <CircularProgress size={16} />}
              </Box>

              {/* Master Switch */}
              <Box display="flex" alignItems="center" justifyContent="space-between" bgcolor="white" p={1.5} borderRadius={2} mb={2} border="1px solid" borderColor="warning.100">
                <Box>
                  <Typography variant="body2" fontWeight={700}>Master Power</Typography>
                  <Typography variant="caption" color="text.secondary">Main Relay Output</Typography>
                </Box>
                <Switch
                  checked={isOn}
                  onChange={(e) => handleControl('POWER', e.target.checked ? 'ON' : 'OFF')}
                  disabled={controlLoading}
                  color="warning"
                />
              </Box>

              {/* Regulator */}
              <Box bgcolor="white" p={1.5} borderRadius={2} border="1px solid" borderColor="warning.100">
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" fontWeight={700}>Intensity</Typography>
                  <Typography variant="caption" fontWeight={700}>{intensity}%</Typography>
                </Box>
                <Slider
                  value={intensity}
                  onChangeCommitted={(_, value) => handleControl('INTENSITY', `${value}%`)}
                  disabled={controlLoading}
                  valueLabelDisplay="auto"
                  step={10}
                  min={0}
                  max={100}
                  sx={{ color: '#f59e0b', height: 6 }}
                />
              </Box>
            </Box>

            {/* DIAGNOSTICS TABLE */}
            <Box p={2} sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" fontWeight={800}>Diagnostics</Typography>
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>, sx: { borderRadius: '12px', bgcolor: 'grey.50' } }}
                  sx={{ width: 140 }}
                />
              </Box>
            </Box>

            <TableContainer sx={{ flexGrow: 1 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 30 }} />
                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '10px' }}>SENSOR</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '10px' }}>VALUE</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '10px' }}>TREND</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSensors.map((sensor) => (
                    <TableRow key={sensor.id} hover>
                      <TableCell padding="none" align="center"><IconButton size="small"><StarBorderIcon sx={{ fontSize: 14 }} /></IconButton></TableCell>
                      <TableCell sx={{ fontWeight: 800, fontSize: '13px' }}>{sensor.name}</TableCell>
                      <TableCell align="center">
                        <Chip label={sensor.value} size="small" variant="outlined" sx={{ fontWeight: 800, fontFamily: 'monospace', borderRadius: '4px', height: 20, fontSize: '11px' }} />
                      </TableCell>
                      <TableCell sx={{ width: 60 }}>
                        <Box sx={{ height: 20, width: 60 }}>
                          <Line
                            data={{
                              labels: sensor.history?.map((_, i) => i) || [],
                              datasets: [{ data: sensor.history || [], borderColor: '#f59e0b', borderWidth: 2, fill: false, pointRadius: 0 }]
                            }}
                            options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { enabled: false } }, scales: { x: { display: false }, y: { display: false } } }}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box p={2} bgcolor="grey.50" display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" color="text.secondary" fontWeight={700}>Sync: {formatToIST(deviceDetails?.timestamp)}</Typography>
            </Box>
          </Card>
        </Grid>

        {/* Right: Service History */}
        <Grid item xs={12} md={12} lg={3}>
          <Card sx={{ height: 874, display: 'flex', flexDirection: 'column' }}>
            <Box p={3} sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Box display="flex" alignItems="center" gap={1}>
                <HistoryIcon />
                <Typography variant="h6" fontWeight={800}>Service History</Typography>
              </Box>
            </Box>
            <CardContent sx={{ flexGrow: 1, overflowY: 'auto', p: 0 }}>
              <Stack spacing={0}>
                {[
                  { date: '12 Feb 2024, 10:00', ago: '2 weeks ago', title: 'Bulb Replacement', hours: '120h', type: 'Corrective', active: true },
                  { date: '10 Jan 2024, 09:30', ago: '1 month ago', title: '100h Inspection', hours: '100h', type: 'Preventive', active: false },
                ].map((item, index) => (
                  <Box key={index} sx={{ position: 'relative', pl: 4, pr: 3, pt: 3, pb: 1, '&:last-child': { pb: 3 } }}>
                    {/* Timeline Line */}
                    {index !== 1 && <Box sx={{ position: 'absolute', left: 29, top: 45, bottom: -45, width: 2, bgcolor: 'grey.200' }} />}

                    {/* Timeline Icon */}
                    <Avatar sx={{ position: 'absolute', left: 16, top: 24, width: 28, height: 28, bgcolor: 'success.50', color: 'success.main', border: '1px solid', borderColor: 'success.100' }}>
                      <AssignmentIcon sx={{ fontSize: 16 }} />
                    </Avatar>

                    <Typography variant="subtitle2" fontWeight={700} color="text.secondary">{item.date} <Typography component="span" variant="caption" color="text.disabled">({item.ago})</Typography></Typography>

                    <Box display="flex" alignItems="center" gap={1} mt={1} mb={2}>
                      <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />
                      <Typography variant="body2" fontWeight={800}>{item.title}</Typography>
                    </Box>

                    <Box bgcolor="grey.50" p={1.5} borderRadius="8px" display="flex" alignItems="center" gap={1.5}>
                      <Avatar sx={{ width: 24, height: 24, bgcolor: 'grey.300' }}><ClockIcon sx={{ fontSize: 14, color: 'grey.700' }} /></Avatar>
                      <Box>
                        <Typography variant="caption" display="block" color="text.secondary" lineHeight={1}>Engine Hours</Typography>
                        <Typography variant="subtitle2" fontWeight={800}>{item.hours}</Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TowerLightPage;